import { useState } from "react";
import { DotLegalBreadCrumbModel } from "@dotlegal/dotlegal-ui-components";
import { EntityTemplateRowTableModel, EntityTemplateRowViewModel, getEntityTemplateTranslationKeys, TemplateEntityType } from "./EntityTemplate.types.ts";
import { useTranslation } from "../localization/useTranslation.ts";
import { useUrlProvider } from "../useUrlProvider.ts";
import { useNavigate, useParams } from "react-router";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useEntityTemplateLockedText } from "./useEntityTemplateLockedText";

export function useEntityTemplates() {
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    const { type } = useParams<{ type: string }>();
    const templateEntityType = Number(type) as TemplateEntityType;
    const { translateString } = useTranslation();
    const { getLockedText } = useEntityTemplateLockedText();

    const url = "/entity-templates?templateEntityType=" + templateEntityType;
    const { isLoading, data: entityTemplates, refetch, isIdle } = useDotLegalQuery<Array<EntityTemplateRowViewModel>>(url);

    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [entityTemplateForDeletion, setEntityTemplateForDeletion] = useState<EntityTemplateRowViewModel | undefined>(undefined);
    const [entityTemplateToEdit, setEntityTemplateToEdit] = useState<EntityTemplateRowViewModel | undefined>(undefined);
    const [entityTemplateToToggleActiveStatus, setEntityTemplateToToggleActiveStatus] = useState<EntityTemplateRowViewModel | undefined>(undefined);

    const breadCrumbs: Array<DotLegalBreadCrumbModel> = [
        { name: translateString("settings"), link: urlProvider.getSettingsUrl() },
        { name: translateString(getEntityTemplateTranslationKeys(templateEntityType).templates) },
    ];

    const onCreateClick = () => {
        setShowCreateDialog(true);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDialogClose = () => {
        setShowCreateDialog(false);
        setEntityTemplateToEdit(undefined);
        setEntityTemplateToToggleActiveStatus(undefined);
    };

    const onDelete = () => {
        // This is now a placeholder method as actual deletion is handled by DeleteContractTemplateDialog
        setEntityTemplateForDeletion(undefined);
        refetch();
    };

    const getMenuItems = (contractTemplate: EntityTemplateRowViewModel) => {
        let menuItems = [];
        menuItems.push({
            menuItem: translateString("open"),
            onClick: () => {
                navigate(urlProvider.getEditEntityTemplate(contractTemplate.id));
            },
        });

        if (!contractTemplate.isStandardTemplate) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setEntityTemplateToEdit(contractTemplate);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setEntityTemplateForDeletion(contractTemplate);
                },
            });
        }

        menuItems.push({
            menuItem: contractTemplate.active ? translateString("deactivate") : translateString("activate"),
            onClick: () => {
                setEntityTemplateToToggleActiveStatus(contractTemplate);
            },
        });

        return menuItems;
    };

    const hasEntityTemplates = isIdle || isLoading || (entityTemplates && entityTemplates.length > 0);
    const tableData: Array<EntityTemplateRowTableModel> = (entityTemplates ?? []).map((row) => ({
        ...row,
        activeStatusString: row.active ? translateString("active") : translateString("deactivated"),
        lockedTooltip: row.hasAccess ? undefined : getLockedText(row.unlockingPlans),
    }));

    return {
        isLoading,
        tableData,
        hasEntityTemplates,
        breadCrumbs,
        getMenuItems,
        showCreateDialog,
        onSaved,
        entityTemplateForDeletion,
        setEntityTemplateForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        entityTemplateToEdit,
        entityTemplateToToggleActiveStatus,
        templateEntityType,
    };
}
